import { useLanguage } from '../context/LanguageContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

const content = {
  de: {
    pageTitle: 'Impressum',
    companyData: 'Firmendaten laut Gewerbedatenbank',
    extendedData: 'Zusätzliche erweiterte Firmendaten',
    legalInfo: 'Rechtliche Informationen',
    idNumbers: 'Kennnummern',
    mediaDisclosure: 'Offenlegung nach Mediengesetz',
    licenses: 'Berechtigungen',
    labels: {
      companyName: 'Firmenname:',
      companyRegNo: 'Firmenbuchnummer:',
      court: 'Firmengericht:',
      businessDesc: 'Geschäftsbezeichnung:',
      address: 'Adresse:',
      legalForm: 'Rechtsform:',
      phone: 'Telefon:',
      web: 'Web:',
      email: 'E-Mail:',
      authority: 'Gewerbebehörde:',
      regulations: 'Anwendbare Rechtsvorschriften:',
      supervisory: 'Weitere Aufsichtsbehörde (gem. ECG):',
      uid: 'UID-Nummer:',
      eori: 'EORI-Nummer:',
      gln: 'GLN Nummer:',
      pic: 'PIC Nummer:',
      duns: 'D-U-N-S Nummer:',
      mediaOwner: 'Medieninhaber:',
      hq: 'Firmensitz:',
      purpose: 'Unternehmensgegenstand:',
      purposeText: 'Erbringung von IT-Dienstleistungen sowie Entwicklung, Konzeptionierung, Beratung und Vertrieb digitaler Schulungslösungen (E-Training), insbesondere maßgeschneiderter E-Training-Module und Unterweisungssysteme für Unternehmen und Organisationen.',
      activity: 'Tätigkeit:',
      manager: 'Gewerberechtliche Geschäftsführung:',
      gisa: 'GISA-Zahl:',
      regSee: 'Gewerbeordnung siehe',
    }
  },
  en: {
    pageTitle: 'Imprint',
    companyData: 'Company Data (Austrian Trade Register)',
    extendedData: 'Extended Company Information',
    legalInfo: 'Legal Information',
    idNumbers: 'Identification Numbers',
    mediaDisclosure: 'Media Disclosure (Austrian Media Act)',
    licenses: 'Trade Licenses',
    labels: {
      companyName: 'Company Name:',
      companyRegNo: 'Company Register No.:',
      court: 'Company Court:',
      businessDesc: 'Business Description:',
      address: 'Address:',
      legalForm: 'Legal Form:',
      phone: 'Phone:',
      web: 'Web:',
      email: 'Email:',
      authority: 'Trade Authority:',
      regulations: 'Applicable Regulations:',
      supervisory: 'Supervisory Body (ECG):',
      uid: 'VAT ID:',
      eori: 'EORI Number:',
      gln: 'GLN Number:',
      pic: 'PIC Number:',
      duns: 'D-U-N-S Number:',
      mediaOwner: 'Media Owner:',
      hq: 'Headquarters:',
      purpose: 'Business Purpose:',
      purposeText: 'Provision of IT services as well as development, conception, consulting and distribution of digital training solutions (e-training), in particular customised e-training modules and instruction systems for companies and organisations.',
      activity: 'Activity:',
      manager: 'Trade Law Manager:',
      gisa: 'GISA Number:',
      regSee: 'Austrian Trade Regulations, see',
    }
  }
};

const licenses = [
  {
    activity: { de: 'Dienstleistungen in der automatischen Datenverarbeitung und Informationstechnik', en: 'Services in automatic data processing and information technology' },
    gisa: '27845769'
  },
  {
    activity: { de: 'Handelsgewerbe mit Ausnahme der reglementierten Handelsgewerbe und Handelsagent', en: 'Trade business with the exception of regulated trade businesses and commercial agents' },
    gisa: '27845868'
  },
  {
    activity: { de: 'Unternehmensberatung einschließlich der Unternehmensorganisation', en: 'Management consulting including business organisation' },
    gisa: '28820222'
  },
  {
    activity: { de: 'Buch-, Kunst- und Musikalienverlag', en: 'Book, art and music publishing' },
    gisa: '29576166'
  },
  {
    activity: { de: 'Werbeagentur', en: 'Advertising agency' },
    gisa: '32937213'
  },
];

export default function ImpressumPage() {
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const t = content[language] || content.de;
  const l = t.labels;

  useEffect(() => {
    if (language === 'en' && location.pathname === '/impressum') {
      navigate('/imprint', { replace: true });
    } else if (language === 'de' && location.pathname === '/imprint') {
      navigate('/impressum', { replace: true });
    }
  }, [language, location.pathname, navigate]);

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="impressum-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <article className="impressum-page">
          <style>{`
            .impressum-page h1 { font-size: 2.75rem; font-weight: 700; margin-bottom: 2.5rem; padding-bottom: 1.5rem; border-bottom: 3px solid #2E5E99; color: #0D2440; }
            .impressum-page .section { margin-bottom: 2.5rem; padding: 1.5rem; background: #f8fafc; border-radius: 0.75rem; border-left: 4px solid #2E5E99; }
            .impressum-page .section-title { font-size: 1.5rem; font-weight: 600; color: #0D2440; margin-bottom: 1.25rem; padding-bottom: 0.75rem; border-bottom: 1px dashed #cbd5e1; }
            .impressum-page .info-grid { display: grid; gap: 0.75rem; }
            .impressum-page .info-row { display: flex; flex-wrap: wrap; gap: 0.5rem; padding: 0.5rem 0; border-bottom: 1px solid #e2e8f0; }
            .impressum-page .info-row:last-child { border-bottom: none; }
            .impressum-page .info-label { font-weight: 600; color: #0D2440; min-width: 200px; }
            .impressum-page .info-value { color: #4a5568; flex: 1; }
            .impressum-page .info-value a { color: #2E5E99; text-decoration: underline; text-underline-offset: 2px; }
            .impressum-page .info-value a:hover { color: #1e4a7a; }
            .impressum-page .description { color: #4a5568; line-height: 1.75; margin-top: 0.75rem; }
            .impressum-page .license-card { background: white; padding: 1.25rem; border-radius: 0.5rem; margin-top: 1rem; border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
            .impressum-page .license-info { color: #4a5568; font-size: 0.95rem; line-height: 1.7; }
            @media (max-width: 640px) { .impressum-page .info-row { flex-direction: column; gap: 0.25rem; } .impressum-page .info-label { min-width: auto; } }
          `}</style>

          <h1>{t.pageTitle}</h1>

          {/* Company Data */}
          <div className="section">
            <h2 className="section-title">{t.companyData}</h2>
            <div className="info-grid">
              <div className="info-row"><span className="info-label">{l.companyName}</span><span className="info-value">e-Matrix Innovations GmbH</span></div>
              <div className="info-row"><span className="info-label">{l.companyRegNo}</span><span className="info-value">435324h</span></div>
              <div className="info-row"><span className="info-label">{l.court}</span><span className="info-value">Landesgericht Wels</span></div>
            </div>
          </div>

          {/* Extended Company Data */}
          <div className="section">
            <h2 className="section-title">{t.extendedData}</h2>
            <div className="info-grid">
              <div className="info-row"><span className="info-label">{l.businessDesc}</span><span className="info-value">E-Learning, Lernmanagementsysteme, IT-Dienstleistungen</span></div>
              <div className="info-row"><span className="info-label">{l.address}</span><span className="info-value">Stelzhamerstra&szlig;e 9, 4600 Wels</span></div>
              <div className="info-row"><span className="info-label">{l.legalForm}</span><span className="info-value">{language === 'de' ? 'Gesellschaft mit beschränkter Haftung' : 'Limited Liability Company (GmbH)'}</span></div>
              <div className="info-row"><span className="info-label">{l.phone}</span><span className="info-value"><a href="tel:+4372429328080">+43 (0)7242 932808</a></span></div>
              <div className="info-row"><span className="info-label">{l.web}</span><span className="info-value"><a href="https://www.e-matrix.at" target="_blank" rel="noopener noreferrer">www.e-matrix.at</a></span></div>
              <div className="info-row"><span className="info-label">{l.email}</span><span className="info-value"><a href="mailto:office@e-matrix.at">office@e-matrix.at</a></span></div>
            </div>
          </div>

          {/* Legal Information */}
          <div className="section">
            <h2 className="section-title">{t.legalInfo}</h2>
            <div className="info-grid">
              <div className="info-row"><span className="info-label">{l.authority}</span><span className="info-value">Magistrat der Stadt Wels</span></div>
              <div className="info-row"><span className="info-label">{l.regulations}</span><span className="info-value">{l.regSee} <a href="https://www.ris.bka.gv.at" target="_blank" rel="noopener noreferrer">www.ris.bka.gv.at</a></span></div>
              <div className="info-row"><span className="info-label">{l.supervisory}</span><span className="info-value">{language === 'de' ? 'Mitglied der Wirtschaftskammer OÖ' : 'Member of the Upper Austrian Chamber of Commerce'}</span></div>
            </div>
          </div>

          {/* ID Numbers */}
          <div className="section">
            <h2 className="section-title">{t.idNumbers}</h2>
            <div className="info-grid">
              <div className="info-row"><span className="info-label">{l.uid}</span><span className="info-value">ATU69683559</span></div>
              <div className="info-row"><span className="info-label">{l.eori}</span><span className="info-value">ATEOS1000104112</span></div>
              <div className="info-row"><span className="info-label">{l.gln}</span><span className="info-value">9110022166927</span></div>
              <div className="info-row"><span className="info-label">{l.pic}</span><span className="info-value">926029125</span></div>
              <div className="info-row"><span className="info-label">{l.duns}</span><span className="info-value">300473023</span></div>
            </div>
          </div>

          {/* Media Disclosure */}
          <div className="section">
            <h2 className="section-title">{t.mediaDisclosure}</h2>
            <div className="info-grid">
              <div className="info-row"><span className="info-label">{l.mediaOwner}</span><span className="info-value">e-Matrix Innovations GmbH</span></div>
              <div className="info-row"><span className="info-label">{l.hq}</span><span className="info-value">4600 Wels</span></div>
            </div>
            <p className="description">
              <strong>{l.purpose}</strong> {l.purposeText}
            </p>
          </div>

          {/* Trade Licenses */}
          <div className="section">
            <h2 className="section-title">{t.licenses}</h2>
            {licenses.map((lic) => (
              <div key={lic.gisa} className="license-card">
                <div className="license-info">
                  <strong>{l.activity}</strong> {lic.activity[language] || lic.activity.de}<br />
                  <strong>{l.gisa}</strong> {lic.gisa}
                </div>
              </div>
            ))}
          </div>

        </article>
      </div>
    </div>
  );
}
